import * as React from 'react';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import type {FilterObject} from 'nutshell-core/types';

import {LoadingPage} from 'shells/loading-page/loading-page';
import {useDebounce} from 'shells/utils/hooks/use-debounce';

import {writeLocalStorage} from '../../../util/localStorage';

import {useGetSetting} from '../../core/use-get-setting';

import {useGetLatestThreads} from '../graphql/hooks';
import {getThreadStatusFromSelectedTab} from '../helpers';
import {InboxPageContent} from '../shared-components/inbox-page-content';
import {useCreateShelly} from '../sms/graphql/hooks';

export const INBOX_SEARCH = 'INBOX_SEARCH';
const SETTING_NAME = 'has_viewed_inbox';
const THREAD_TYPE = 'SMS';

type Props = ContextRouter & {
    activeFilters: FilterObject[];
    onParamChange: (arg1: any, arg2: boolean) => any;
    onRemoveAllFilters: () => void;
    assigneeIds: string[];
};

export function SMSPage(props: Props) {
    const selectedPage = props.match.params.page;
    const status = getThreadStatusFromSelectedTab(selectedPage);
    const [search, setSearch] = React.useState('');
    const debouncedSearch = useDebounce(search, 250);

    const initialThreadId = React.useRef(props.match.params.id).current;
    const {
        latestThreads,
        totalCount,
        isLoading: isLoadingLatestThreads,
        fetchMoreRows: fetchMoreThreads,
        hasNextPage: hasMoreThreads,
        refetch: refetchThreads,
    } = useGetLatestThreads({
        status,
        assignees: props.assigneeIds,
        threadType: THREAD_TYPE,
        findThreadId: initialThreadId || undefined,
        search: debouncedSearch || undefined,
    });
    const {setting, isLoading: isLoadingSetting} = useGetSetting({
        settingName: SETTING_NAME,
        isGlobal: false,
        fetchPolicy: 'cache-first',
    });
    const {createShelly, isLoading: isLoadingShelly} = useCreateShelly(status, props.assigneeIds);

    React.useEffect(() => {
        if (setting && setting.value !== '1') {
            createShelly();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setting, isLoadingSetting]);

    React.useEffect(() => {
        writeLocalStorage(INBOX_SEARCH, props.location.search);
    }, [props.location.search]);

    if (isLoadingShelly || isLoadingSetting) {
        return <LoadingPage />;
    }

    return (
        <InboxPageContent
            {...props}
            selectedView={selectedPage}
            latestThreads={latestThreads}
            latestThreadsTotalCount={totalCount}
            isLoadingLatestThreads={isLoadingLatestThreads}
            fetchMoreThreads={fetchMoreThreads}
            hasMoreThreads={hasMoreThreads}
            refetchThreads={refetchThreads}
            assigneeIds={props.assigneeIds}
            threadType={THREAD_TYPE}
            search={search}
            onSearchChange={setSearch}
        />
    );
}
